import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { format } from "timeago.js";
import "./CommentThread.css";

const IMG_BASE = process.env.REACT_APP_API_URL?.replace("/api", "") ?? "";

function AvatarImg({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`${className} avatar-placeholder`}>
        <span className="material-symbols-rounded">person</span>
      </div>
    );
  }
  return (
    <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />
  );
}

function CommentThread({
  comments,
  highlightedText,
  focusCommentId,
  chapterTitle = "",
  onCommentDeleted,
  onReplyAdded,
  onCommentLiked,
  onCommentsReload
}) {
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [replyTexts, setReplyTexts] = useState({});
  const [localComments, setLocalComments] = useState(comments);
  const [replyingTo, setReplyingTo] = useState(null);
  const [isReplyClosing, setIsReplyClosing] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const replyTextareaRef = useRef(null);
  const commentRefs = useRef(new Map());
  const replyCloseTimerRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = currentUser.id;
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiFetch = (url, options = {}) =>
    {
      const rememberToken = localStorage.getItem("rememberToken") || "";
      const nextHeaders = {
        ...(options.headers || {})
      };
      if (rememberToken && !nextHeaders["X-Remember-Token"]) {
        nextHeaders["X-Remember-Token"] = rememberToken;
      }
      return fetch(url, {
        credentials: "include",
        cache: "no-store",
        ...options,
        headers: nextHeaders
      });
    };

  useEffect(() => {
    setLocalComments(comments || []);
  }, [comments]);

  useEffect(() => {
    if (!focusCommentId) return;
    const target = commentRefs.current.get(focusCommentId);
    if (!target?.scrollIntoView) return;
    const timer = window.setTimeout(() => {
      target.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focusCommentId, localComments]);

  useEffect(() => {
    if (!replyingTo || !replyTextareaRef.current) return;
    window.requestAnimationFrame(() => {
      replyTextareaRef.current.focus({ preventScroll: true });
      autoResizeTextarea(replyTextareaRef.current);
    });
  }, [replyingTo]);

  useEffect(() => {
    if (!replyingTo) {
      setKeyboardOffset(0);
      return;
    }

    const scrollY = window.scrollY;
    const previousBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow
    };

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    const viewport = window.visualViewport;
    const updateKeyboardOffset = () => {
      if (!viewport) {
        setKeyboardOffset(0);
        return;
      }
      const offset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardOffset(offset);
    };

    updateKeyboardOffset();
    viewport?.addEventListener("resize", updateKeyboardOffset);
    viewport?.addEventListener("scroll", updateKeyboardOffset);

    return () => {
      viewport?.removeEventListener("resize", updateKeyboardOffset);
      viewport?.removeEventListener("scroll", updateKeyboardOffset);
      document.body.style.position = previousBodyStyle.position;
      document.body.style.top = previousBodyStyle.top;
      document.body.style.width = previousBodyStyle.width;
      document.body.style.overflow = previousBodyStyle.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [replyingTo]);

  useEffect(() => {
    return () => {
      window.clearTimeout(replyCloseTimerRef.current);
    };
  }, []);

  const closeReplyModal = () => {
    if (!replyingTo || isReplyClosing) return;
    setIsReplyClosing(true);
    window.clearTimeout(replyCloseTimerRef.current);
    replyCloseTimerRef.current = window.setTimeout(() => {
      setReplyingTo(null);
      setIsReplyClosing(false);
    }, 160);
  };

  const replyChildrenMap = useMemo(() => {
    const map = new Map();
    localComments.forEach((comment) => {
      (comment.replies || []).forEach((reply) => {
        const key = reply.parent_reply_id || "root";
        if (!map.has(comment.id)) map.set(comment.id, new Map());
        const commentMap = map.get(comment.id);
        if (!commentMap.has(key)) commentMap.set(key, []);
        commentMap.get(key).push(reply);
      });
    });
    return map;
  }, [localComments]);

  const toggleExpanded = (commentId) => {
    const newSet = new Set(expandedComments);
    if (newSet.has(commentId)) {
      newSet.delete(commentId);
    } else {
      newSet.add(commentId);
    }
    setExpandedComments(newSet);
  };

  const openReplyBox = ({
    commentId,
    replyId = null,
    displayName,
    profilePicture = null,
    text = ""
  }) => {
    setIsReplyClosing(false);
    window.clearTimeout(replyCloseTimerRef.current);
    setReplyingTo({ commentId, replyId, displayName, profilePicture, text });
  };

  const updateReplyInList = (replies, replyId, updater) =>
    replies.map((reply) => (reply.id === replyId ? updater(reply) : reply));

  const autoResizeTextarea = (element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  };

  const collectReplyIds = (replies, rootId) => {
    const ids = [rootId];
    for (let index = 0; index < ids.length; index++) {
      replies.forEach((reply) => {
        if (reply.parent_reply_id === ids[index]) {
          ids.push(reply.id);
        }
      });
    }
    return ids;
  };

  const handleLike = async (commentId) => {
    try {
      const form = new FormData();
      form.append("uid", userId);
      form.append("comment_id", commentId);
      form.append("chapter_title", chapterTitle || "");
      const response = await apiFetch(`${apiUrl}/like-comment.php`, {
        method: "POST",
        body: form
      });
      const data = await response.json();
        if (data.success) {
          setLocalComments((prev) =>
            prev.map((comment) =>
              comment.id === commentId
                ? { ...comment, user_liked: data.liked, like_count: data.like_count }
              : comment
          )
          );
          onCommentLiked?.(commentId, data.liked, data.like_count);
          window.dispatchEvent(new CustomEvent("notifications-updated"));
        }
      } catch (error) {
        console.error("Error liking comment:", error);
      }
  };

  const handleReplyLike = async (commentId, replyId) => {
    try {
      const form = new FormData();
      form.append("uid", userId);
      form.append("reply_id", replyId);
      const response = await apiFetch(`${apiUrl}/like-reply.php`, {
        method: "POST",
        body: form
      });
      const data = await response.json();
      if (data.success) {
        setLocalComments((prev) =>
          prev.map((comment) =>
            comment.id === commentId
              ? {
                  ...comment,
                  replies: updateReplyInList(comment.replies || [], replyId, (reply) => ({
                    ...reply,
                    user_liked: data.liked,
                    like_count: data.like_count
                  }))
                }
              : comment
          )
        );
        onCommentsReload?.();
      }
    } catch (error) {
      console.error("Error liking reply:", error);
    }
  };

  const handleReplySubmit = async () => {
    if (!replyingTo) return;
    const text = replyTexts[replyingTo.replyId || replyingTo.commentId]?.trim();
    if (!text) return;

    try {
      const form = new FormData();
      form.append("uid", userId);
      form.append("comment_id", replyingTo.commentId);
      form.append("text", text);
      form.append("chapter_title", chapterTitle || "");
      if (replyingTo.replyId) {
        form.append("parent_reply_id", replyingTo.replyId);
      }

      const response = await apiFetch(`${apiUrl}/add-reply.php`, {
        method: "POST",
        body: form
      });
      const data = await response.json();
        if (data.success) {
          setLocalComments((prev) =>
            prev.map((comment) =>
              comment.id === replyingTo.commentId
                ? {
                  ...comment,
                  replies: [...(comment.replies || []), data.reply],
                  reply_count: Number(comment.reply_count || 0) + 1
                }
              : comment
          )
        );
          onReplyAdded?.(replyingTo.commentId, data.reply);
          window.dispatchEvent(new CustomEvent("notifications-updated"));
          setReplyTexts((prev) => ({
            ...prev,
            [replyingTo.replyId || replyingTo.commentId]: ""
          }));
        closeReplyModal();
      }
    } catch (error) {
      console.error("Error adding reply:", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;

    try {
      const form = new FormData();
      form.append("uid", userId);
      form.append("comment_id", commentId);
      const response = await apiFetch(`${apiUrl}/delete-comment.php`, {
        method: "POST",
        body: form
      });
      const data = await response.json();
      if (data.success) {
        setLocalComments((prev) => prev.filter((comment) => comment.id !== commentId));
        onCommentDeleted?.(commentId);
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handleDeleteReply = async (commentId, replyId) => {
    if (!window.confirm("Delete this reply?")) return;

    try {
      const form = new FormData();
      form.append("uid", userId);
      form.append("reply_id", replyId);
      const response = await apiFetch(`${apiUrl}/delete-reply.php`, {
        method: "POST",
        body: form
      });
      const data = await response.json();
      if (data.success) {
        setLocalComments((prev) =>
          prev.map((comment) => {
            if (comment.id !== commentId) return comment;
            const removedIds = collectReplyIds(comment.replies || [], replyId);
            return {
              ...comment,
              replies: (comment.replies || []).filter((reply) => !removedIds.includes(reply.id)),
              reply_count: Math.max(0, Number(comment.reply_count || 0) - removedIds.length)
            };
          })
        );
        onCommentsReload?.();
      }
    } catch (error) {
      console.error("Error deleting reply:", error);
    }
  };

  const renderReplies = (comment, parentReplyId = null) => {
    const commentMap = replyChildrenMap.get(comment.id);
    const replies = commentMap?.get(parentReplyId || "root") || [];

    return replies.flatMap((reply) => [
      <div key={reply.id} className="comment-reply">
        <div className="reply-header">
          <AvatarImg
            src={reply.profile_picture ? `${IMG_BASE}/${reply.profile_picture}` : null}
            alt={reply.display_name}
            className="reply-avatar"
          />
          <div className="reply-text-info">
            <span className="reply-user-name">
              {reply.display_name || `User #${reply.user_id}`}
            </span>
            <span className="reply-action">
              replied to {reply.replying_to_display_name || comment.display_name}
            </span>
          </div>
        </div>

        <div className="reply-text">{reply.text}</div>

        <div className="reply-actions">
          <button
            className="comment-reply-btn"
            onClick={() =>
              openReplyBox({
                commentId: comment.id,
                replyId: reply.id,
                displayName: reply.display_name || `User #${reply.user_id}`,
                profilePicture: reply.profile_picture || null,
                text: reply.text || ""
              })
            }
          >
            <span className="material-symbols-rounded">reply</span>
            <span className="comment-reply-count">{reply.reply_count ?? 0}</span>
          </button>

          <button
            className={`comment-like-btn${reply.user_liked ? " liked" : ""}`}
            onClick={() => handleReplyLike(comment.id, reply.id)}
          >
            <span className="material-symbols-rounded">favorite</span>
            <span className="comment-like-count">{reply.like_count ?? 0}</span>
          </button>

          <span className="comment-date">{format(reply.created_at)}</span>

          {reply.user_id === userId && (
            <button
              className="comment-delete-btn"
              onClick={() => handleDeleteReply(comment.id, reply.id)}
            >
              Delete
            </button>
          )}
        </div>

      </div>,
      ...renderReplies(comment, reply.id)
    ]);
  };

  const replyModal =
    replyingTo &&
    createPortal(
      <div
        className={`reply-modal-overlay${isReplyClosing ? " reply-modal-overlay--closing" : ""}`}
        onClick={closeReplyModal}
      >
        <div
          className={`reply-modal${isReplyClosing ? " reply-modal--closing" : ""}`}
          onClick={(e) => e.stopPropagation()}
          style={{ transform: `translateY(-${keyboardOffset}px)` }}
        >
          <div className="reply-modal-header">
            <div className="reply-modal-user">
              <AvatarImg
                src={replyingTo.profilePicture ? `${IMG_BASE}/${replyingTo.profilePicture}` : null}
                alt={replyingTo.displayName}
                className="reply-avatar"
              />
              <div className="reply-modal-title">
                <span className="reply-modal-prefix">replying to </span>
                <span className="reply-modal-name">
                  {replyingTo.displayName || "User"}
                </span>
              </div>
            </div>
            <button
              className="reply-modal-submit"
              onClick={handleReplySubmit}
              disabled={!replyTexts[replyingTo.replyId || replyingTo.commentId]?.trim()}
            >
              Reply
            </button>
          </div>

          <div className="reply-modal-body">
            <textarea
              ref={replyTextareaRef}
              className="reply-textarea reply-textarea--modal"
              placeholder={`Reply to ${replyingTo.displayName || "this thread"}...`}
              value={replyTexts[replyingTo.replyId || replyingTo.commentId] || ""}
              rows={1}
              onChange={(e) =>
                setReplyTexts((prev) => ({
                  ...prev,
                  [replyingTo.replyId || replyingTo.commentId]: e.target.value
                }))
              }
              onInput={(e) => autoResizeTextarea(e.currentTarget)}
            />
          </div>
        </div>
      </div>,
      document.body
    );

  if (!comments || comments.length === 0) {
    return <div className="comment-thread-empty">No comments yet</div>;
  }

  return (
    <div className="comment-thread">
      {localComments.map((comment) => (
        <div
          key={comment.id}
          className="comment-item"
          ref={(node) => {
            if (node) commentRefs.current.set(comment.id, node);
            else commentRefs.current.delete(comment.id);
          }}
        >
          <div className="comment-header">
            <div className="comment-user-info">
              <AvatarImg
                src={comment.profile_picture ? `${IMG_BASE}/${comment.profile_picture}` : null}
                alt={comment.display_name}
                className="comment-avatar"
              />
              <div className="comment-text-info">
                <span className="comment-user-name">
                  {comment.display_name || `User #${comment.user_id}`}
                </span>
                <span className="comment-action">commented on:</span>
              </div>
            </div>
          </div>

          {(comment.selected_excerpt || highlightedText) && (
            <div className="comment-highlight-section">
              <div className="comment-highlight-label">Highlight:</div>
              <div className="comment-highlight-text">
                {comment.selected_excerpt || highlightedText}
              </div>
            </div>
          )}

          <div className="comment-text">{comment.text}</div>

          <div className="comment-actions">
            <button
              className="comment-reply-btn"
              onClick={() =>
                openReplyBox({
                  commentId: comment.id,
                  displayName: comment.display_name || `User #${comment.user_id}`,
                  profilePicture: comment.profile_picture || null,
                  text: comment.text || ""
                })
              }
            >
              <span className="material-symbols-rounded">reply</span>
            </button>

            <button
              className={`comment-like-btn${comment.user_liked ? " liked" : ""}`}
              onClick={() => handleLike(comment.id)}
            >
              <span className="material-symbols-rounded">favorite</span>
              <span className="comment-like-count">{comment.like_count}</span>
            </button>

            <span className="comment-date">{format(comment.created_at)}</span>

            {comment.user_id === userId && (
              <button
                className="comment-delete-btn"
                onClick={() => handleDeleteComment(comment.id)}
              >
                Delete
              </button>
            )}

          </div>

          {comment.reply_count > 0 && (
            <button
              className="view-replies-btn"
              onClick={() => toggleExpanded(comment.id)}
            >
              {expandedComments.has(comment.id)
                ? "Hide replies"
                : `View ${comment.reply_count} ${comment.reply_count === 1 ? "reply" : "replies"}`}
            </button>
          )}

          {expandedComments.has(comment.id) && (
            <div className="comment-replies-section">
              <div className="comment-replies">{renderReplies(comment)}</div>
            </div>
          )}
        </div>
      ))}

      {replyModal}
    </div>
  );
}

export default CommentThread;
